import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTradingPositionListPositions } from '@/lib/endpoints';
import { PlaeePositionResponseDtoStatus, TradingPositionListPositionsStatus } from '@/lib/models';
import type { EventDetailMarketDto } from '@/lib/models';
import { fmtPrice, signedUsd } from '@/lib/trading/format';
import { positionLabeler } from '@/lib/trading/position-label';
import { cn } from '@/lib/utils';
import { CollapsibleSection } from './CollapsibleSection';

export interface MarketHistoryProps {
  /** The event's markets — resolve the market id scope + answer labels (present live AND closed). */
  markets: EventDetailMarketDto[];
  /** Section title (admin-ordered). */
  title: string;
  /** Initial expanded state. */
  defaultOpen?: boolean;
  /** Expand/collapse analytics. */
  onToggle?: (open: boolean) => void;
}

/** A finished position rendered as one history row. */
interface HistoryRow {
  id: string;
  /** Answer label, e.g. "JD Vance" or "Yes". */
  answer: string;
  /** Realized profit/loss in dollars (negative = loss). */
  pnl: number;
  /** Average entry price label, e.g. `62¢`. */
  avgPrice: string;
  /** Outcome: `Won`/`Lost` for a settled market, `Sold` when the user exited early. */
  result: 'Won' | 'Lost' | 'Sold';
}

/** Rows shown before "Show More" expands the list (Figma 6538-187014). */
const COLLAPSED_ROW_COUNT = 4;

/**
 * My History section for the binary market page (ARC-82 §5.4.2; columns per
 * the resolution mocks, Figma 6538-186912/187014: Answer · P&L · Avg Price ·
 * Result, with Show More past {@link COLLAPSED_ROW_COUNT} rows).
 *
 * The customer's **finished** positions in this event (SETTLED by market
 * resolution, or CLOSED because they sold out early) — realized P&L, average
 * entry price, and result. Scoped to the event's **market id(s)**, NOT its
 * options' outcome ids: settlement excludes an outcome from `options` (and a
 * closed page has no options at all), so scoping by outcome would hide exactly
 * the positions we want to show. A position keeps its `externalMarketId`
 * through settlement, so the market scope captures resolved (incl. eliminated)
 * candidates.
 */
const FINISHED = `${TradingPositionListPositionsStatus.closed},${TradingPositionListPositionsStatus.settled}`;

export function MarketHistory({ markets, title, defaultOpen, onToggle }: MarketHistoryProps) {
  const [showAll, setShowAll] = useState(false);
  const marketIds = [...new Set(markets.map((m) => m.externalMarketId))].join(',');

  // The statuses param already scopes the fetch to finished positions.
  const { data: positions = [] } = useTradingPositionListPositions(
    { statuses: FINISHED, marketIds },
    { query: { enabled: Boolean(marketIds) } },
  );

  const labelFor = positionLabeler(markets);
  const rows: HistoryRow[] = positions.map((p) => ({
    id: p.positionId,
    answer: labelFor(p),
    pnl: p.realizedPnl,
    avgPrice: fmtPrice(p.avgEntryPrice ?? 0),
    // CLOSED = exited early ("Sold"); SETTLED = resolved by the market (Won/Lost).
    result: p.status === PlaeePositionResponseDtoStatus.CLOSED ? 'Sold' : p.realizedPnl >= 0 ? 'Won' : 'Lost',
  }));
  const realized = rows.reduce((sum, r) => sum + r.pnl, 0);
  const visibleRows = showAll ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);

  return (
    <CollapsibleSection
      title={title}
      defaultOpen={defaultOpen}
      onToggle={onToggle}
      disabled={rows.length === 0}
      summary={
        rows.length > 0 ? (
          <span
            className={cn(
              'rounded-pill px-2.5 py-0.5 text-sm font-medium capitalize tracking-[-0.28px]',
              realized >= 0 ? 'bg-up/10 text-up' : 'bg-down/10 text-down',
            )}
          >
            Realized {signedUsd(realized)}
          </span>
        ) : (
          <span className="text-muted">No history yet</span>
        )
      }
    >
      <div className="flex flex-col gap-2">
        <div className={cn(HISTORY_ROW_GRID, 'px-3.5 text-sm text-ink-muted/70 tracking-[-0.28px]')}>
          <span>Answer</span>
          <span>P&L</span>
          <span className="text-center whitespace-nowrap">Avg Price</span>
          <span className="text-right">Result</span>
        </div>
        <div className="border-t border-border" />
        <div className="flex flex-col gap-2">
          {visibleRows.map((row) => (
            <TradeRow key={row.id} row={row} />
          ))}
        </div>
        {rows.length > COLLAPSED_ROW_COUNT && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="mx-auto inline-flex items-center gap-1 bg-transparent border-none text-lg font-normal text-purple underline tracking-[-0.36px] cursor-pointer"
          >
            Show More
            <ChevronDown className="size-4" />
          </button>
        )}
      </div>
    </CollapsibleSection>
  );
}

/** Shared column grid for the history rows and their header (Figma 1728-78844). */
const HISTORY_ROW_GRID =
  'grid grid-cols-[minmax(0,1.3fr)_minmax(56px,1fr)_minmax(64px,0.9fr)_minmax(44px,auto)] items-center gap-2';

/**
 * One finished-position row (Figma 1728-78844): a neutral surface-soft tile —
 * only the P&L figure and the Result verdict carry colour (Won green / Lost
 * red; Sold follows the realized P&L sign, a detail the design doesn't cover).
 */
function TradeRow({ row }: { row: HistoryRow }) {
  const gain = row.pnl >= 0;
  const tone = gain ? 'text-up' : 'text-down';
  return (
    <div className={cn(HISTORY_ROW_GRID, 'h-[61px] rounded-[12px] bg-surface-soft px-3.5 text-base')}>
      <span className="truncate font-medium text-ink">{row.answer}</span>
      <span className={cn('font-medium tabular-nums tracking-[-0.32px]', tone)}>{signedUsd(row.pnl)}</span>
      <span className="text-center font-medium tabular-nums text-ink tracking-[-0.32px]">{row.avgPrice}</span>
      <span className={cn('text-right font-semibold capitalize', tone)}>{row.result}</span>
    </div>
  );
}
